import {
    Badge,
    Button,
    Offcanvas,
    OffcanvasBody,
    OffcanvasHeader,
} from 'reactstrap';
import { useContext, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { CampaignContext } from '../../context/campaign';
import {
    deleteFundraiser,
    duplicateEvent,
    fetchMyCampaign,
} from '../../services/campaign';
import {
    MyCampaignResponse,
    Fundraiser,
    FundraiserListResults,
} from '../../types/campaign';
import TableContainer from '../Campaign/components/TableContainer';
import { CampaignHeaderSection } from '../../shared/components/cards/CampaignItemsCard';
import { renderIcon } from '../../shared/components/icons';
import { ColumnDef } from '@tanstack/react-table';
import { fetchFundraisers } from '~/services/fundraisers';
import { Loading } from '~/shared/components/Loading';
import { ConfirmModal } from '~/shared/components/ConfirmModal';
import toast from 'react-hot-toast';
import { AdminContext } from '~/context/admin';
import { fundraiserV2Enabled } from '~/utils/fundraiserBeta';

interface FundraiserEvent {
    title: string;
    event_date: string | null;
    date_closes: string | null;
    time_text: string;
    status: 'closed' | 'registration_closed' | 'open' | 'inactive' | string;
    registered: number;
    is_full: boolean;
    public: boolean;
    event_id: number;
    form_version: number;
    fundraiser_type: 'event' | 'sale';
}

export const Fundraisers = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [filterDrawer, setFilterDrawer] = useState<boolean>(false);
    const [tableLoader, setTableLoader] = useState<boolean>(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const fundraiserBeta = fundraiserV2Enabled(isAdmin);
    const [fundraisers, setFundraisers] = useState<FundraiserListResults>(null);
    const history = useHistory();
    const [page, setPage] = useState(1);
    const [deleteEntry, setDeleteEntry] = useState<FundraiserEvent | null>(
        null
    );
    const [copyEntry, setCopyEntry] = useState<FundraiserEvent | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState(20);
    const [status, setStatus] = useState<string | null>(null);

    function HeaderCard() {
        return (
            <div>
                <h1 className='h2'>Fundraisers</h1>
                <Button color='primary' size='sm' onClick={handleAddFundraiser}>
                    {renderIcon('add')} Add Fundraiser
                </Button>
            </div>
        );
    }

    const Statistics = () => {
        const stats = {
            all: fundraisers?.all_events || 0,
            open: fundraisers?.open_events || 0,
            closed: fundraisers?.closed_events || 0,
        };
        return (
            <>
                <Button
                    color={status === null ? 'warning' : ''}
                    className='text-capitalize'
                    onClick={() => {
                        setPage(1);
                        setStatus(null);
                    }}
                >
                    All Fundraisers <Badge color='dark'>{stats.all}</Badge>
                </Button>
                <Button
                    color={status === 'open' ? 'warning' : ''}
                    className='text-capitalize'
                    onClick={() => {
                        setPage(1);
                        setStatus('open');
                    }}
                >
                    Open/Active <Badge color='dark'>{stats.open}</Badge>
                </Button>
                <Button
                    color={status === 'closed' ? 'warning' : ''}
                    className='text-capitalize'
                    onClick={() => {
                        setPage(1);
                        setStatus('closed');
                    }}
                >
                    Closed/Inactive <Badge color='dark'>{stats.closed}</Badge>
                </Button>
            </>
        );
    };

    const handleAddFundraiser = () => {
        // Always create v1 fundraisers from this page. v2 creation lives behind
        // the separate v2 nav item, which superadmins reach via the beta flag.
        history.push('/fundraisers/add');
    };

    const getEditUrl = (eventId: number, formVersion: number) => {
        return formVersion >= 2
            ? `/fundraisers/v2/edit/${eventId}`
            : `/fundraisers/edit/${eventId}`;
    };

    const data = useMemo(() => {
        if (fundraisers) {
            return fundraisers?.results?.map((fundraiser) => {
                let date = '';
                let close_date = '';
                if (fundraiser.event_date) {
                    date += format(parseISO(fundraiser.event_date), 'MM/dd/yy');
                }
                // if (fundraiser.event_time_text) {
                //   date += `, ${fundraiser.event_time_text}`;
                // }
                if (fundraiser.registration_close_date) {
                    close_date = format(
                        parseISO(fundraiser.registration_close_date),
                        'MM/dd/yy'
                    );
                }
                return {
                    title: fundraiser.name,
                    event_date: date,
                    date_closes: close_date,
                    time_text: fundraiser.event_time_text,
                    status: fundraiser.status,
                    registered: fundraiser.registration_total,
                    is_full: fundraiser.is_full ?? false,
                    public: fundraiser.published,
                    event_id: fundraiser.id,
                    form_version: fundraiser.form_version,
                    fundraiser_type: fundraiser.fundraiser_type,
                };
            });
        } else {
            return [];
        }
    }, [fundraisers]);

    const onDeleteUpdate = async () => {
        try {
            setSaving(true);
            if (deleteEntry) {
                const res = await deleteFundraiser(deleteEntry);

                if (res.status === 204) {
                    campaignDispatch({
                        type: 'remove_event',
                        payload: deleteEntry,
                    });
                    setDeleteEntry(null);
                }
            }
        } catch (error) {
            toast.error('Problem occured while deleting fundraiser!');
        } finally {
            setSaving(false);
        }
    };

    const onCopyConfirm = async () => {
        if (!copyEntry) return;
        try {
            setSaving(true);
            const convertToV2 = fundraiserBeta && copyEntry.form_version < 2;
            const copy = await duplicateEvent(copyEntry.event_id, convertToV2);
            campaignDispatch({
                type: 'add_event',
                payload: copy,
            });
            setCopyEntry(null);
            if (copy?.id) {
                const copyFormVersion = convertToV2 ? 2 : copyEntry.form_version;
                history.push(getEditUrl(copy.id, copyFormVersion));
            }
        } catch (error) {
            toast.error('Problem occurred while copying fundraiser!');
        } finally {
            setSaving(false);
        }
    };

    const columns = useMemo<ColumnDef<FundraiserEvent>[]>(
        () => [
            {
                header: 'Fundraiser Title',
                accessorKey: 'title', // accessor is the "key" in the data
                enableSorting: true, // this is a necessary prop within react-table FYI
                accessorFn: (props) => {
                    return (
                        <Link to={getEditUrl(props.event_id, props.form_version)}>
                            {props?.title}
                        </Link>
                    );
                },
            },
            {
                header: 'Date',
                accessorKey: 'event_date',
                enableSorting: true,
                accessorFn: (props) => {
                    return (
                        <>
                            <div>{props?.event_date}</div>
                            <div className='small text-secondary'>
                                {props?.time_text}
                            </div>
                        </>
                    );
                },
            },
            {
                header: 'Closes',
                accessorKey: 'date_closes',
                enableSorting: true,
            },
            {
                id: 'status',
                header: 'Status',
                accessorKey: 'status',
                enableSorting: true,
                accessorFn: (props) => {
                    return (
                        <Badge
                            color='none'
                            style={{
                                backgroundColor: `${
                                    props?.status === 'Open'
                                        ? '#01aeb4'
                                        : '#952e46'
                                }`,
                            }}
                        >
                            {props?.status}
                        </Badge>
                    );
                },
            },
            {
                header: 'Total',
                accessorKey: 'registered',
                enableSorting: false,
                accessorFn: (props) => {
                    const label = props?.fundraiser_type === 'sale'
                        ? 'Sales'
                        : 'Registrants';
                    return (
                        <div className="d-flex align-items-center gap-2">
                            <Link
                                to={`/fundraisers/registrations/${props.event_id}`}
                            >
                                {props?.registered} {label}
                            </Link>
                            {props?.is_full && (
                                <span className="badge bg-danger">Full</span>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'public',
                header: 'Public',
                accessorKey: 'public',
                enableSorting: false,
                accessorFn: (props) => {
                    return (
                        <div
                            className='text-primary'
                            style={{ fontSize: '20px' }}
                        >
                            {props?.public === true ? renderIcon('check') : ''}
                        </div>
                    );
                },
            },
            {
                id: 'actions',
                header: '',
                accessorKey: 'actions',
                enableSorting: false,
                accessorFn: (props) => {
                    return (
                        <div className='d-lg-flex'>
                            <Button
                                color='white'
                                className='p-1 m-0'
                                style={{ fontSize: '20px' }}
                                onClick={() => {
                                    history.push(getEditUrl(props.event_id, props.form_version));
                                }}
                            >
                                {renderIcon('edit')}
                            </Button>
                            <Button
                                color='white'
                                className='p-1 m-0'
                                style={{ fontSize: '20px' }}
                                onClick={() => setCopyEntry(props)}
                            >
                                {renderIcon('copy')}
                            </Button>
                            <Button
                                color='white'
                                className='p-1 m-0 text-danger'
                                style={{ fontSize: '20px' }}
                                onClick={async () => {
                                    setDeleteEntry(props.event_id);
                                }}
                            >
                                {renderIcon('delete')}
                            </Button>
                        </div>
                    );
                },
            },
        ],
        []
    );

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        }
    }, []);

    const getFundraisers = async () => {
        try {
            setLoading(true);
            const resp = await fetchFundraisers(
                page,
                pageSize,
                status,
                campaignId
            );

            setFundraisers(resp);
            setLoading(false);
        } catch (e) {
            console.log('getFundraisers() e: ', e);
            // Show some error here
        } finally {
            setTableLoader(false);
        }
    };

    useEffect(() => {
        if (campaign) {
            console.log('getting fundraisers');
            getFundraisers();
        }
    }, [campaign, page, pageSize, status]);

    const onPageNumberChange = (page: number) => {
        setTableLoader(true);
        setPage(page);
    };

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            }
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            setLoading(false);
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    if (!campaign || !fundraisers) {
        return <Loading />;
    }

    const { campaignCoordinator } = campaign;

    return (
        <div className='container__inner'>
            <CampaignHeaderSection
                leftcard={<HeaderCard />}
                contact={campaignCoordinator}
            />

            <div className='filter-bar'>
                <div className='d-none d-lg-flex gap-2 align-items-center flex-shrink-0'>
                    <Statistics />
                </div>

                <Button
                    color='outline-dark'
                    className='d-block d-lg-none w-100 m-0'
                    onClick={() => setFilterDrawer(true)}
                >
                    Filters
                </Button>
                <Offcanvas
                    direction='end'
                    fade={false}
                    isOpen={filterDrawer}
                    toggle={() => {
                        setFilterDrawer(false);
                    }}
                >
                    <OffcanvasHeader
                        className='text-uppercase'
                        toggle={() => {
                            setFilterDrawer(false);
                        }}
                    >
                        Filters
                    </OffcanvasHeader>
                    <OffcanvasBody>
                        <div className='d-flex flex-column gap-2'>
                            <Statistics />
                        </div>
                    </OffcanvasBody>
                </Offcanvas>
            </div>

            <div className='pb-5'>
                {loading ? (
                    <Loading />
                ) : (
                    <TableContainer
                        isServerSidePagination={true}
                        showHeaders
                        columns={columns}
                        data={data}
                        showPaginationComponents={true}
                        showFilterComponents={false}
                        sort={false}
                        page={page}
                        pageCount={Math.ceil(fundraisers?.count / pageSize)}
                        tableLoader={tableLoader}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        setPage={onPageNumberChange}
                    />
                )}
            </div>

            <ConfirmModal
                isOpen={!!copyEntry}
                title='Copy Fundraiser?'
                message={
                    fundraiserBeta && copyEntry?.form_version < 2
                        ? 'This fundraiser will be copied and converted to the new format (v2). Registration items will become registration options.'
                        : 'Are you sure you wish to copy this fundraiser?'
                }
                toggle={() => setCopyEntry(null)}
                handleConfirm={onCopyConfirm}
                saving={saving}
            />

            <ConfirmModal
                isOpen={!!deleteEntry}
                title='Delete Fundraiser?'
                message='Are you sure you wish to delete this fundraiser?'
                toggle={() => setDeleteEntry(null)}
                handleConfirm={onDeleteUpdate}
                saving={saving}
                isDeleting={true}
            />
        </div>
    );
};
